import { supabase } from '../lib/supabase';
import { applyDomainFilterToQuery, applyDomainOnlyFilterToQuery } from '../hooks/useDomainQuery';

export interface BlogPost {
  id: string;
  title: string;
  slug: string;
  excerpt: string;
  content: string;
  featured_image_url: string;
  author_id: string;
  status: string;
  created_at: string;
  updated_at: string;
  views_count: number;
  likes_count: number;
  categories?: { id: string; name: string }[];
}

export interface BlogCategory {
  id: string;
  name: string;
  slug: string;
  description?: string;
  post_count?: number;
}

export interface BlogComment {
  id: string;
  post_id: string;
  author_name: string;
  author_email: string;
  content: string;
  status: string;
  created_at: string;
}

/**
 * Récupère tous les articles de blog publiés
 */
export const fetchBlogPosts = async (categoryId?: string, searchQuery?: string) => {
  try {
    // Utiliser la logique domain -> tenant_id fallback
    let query = supabase
      .from('blog_posts')
      .select(`
        id,
        title,
        slug,
        excerpt,
        content,
        featured_image_url,
        author_id,
        status,
        created_at,
        updated_at,
        views_count,
        likes_count
      `)
      .eq('status', 'published')
      .order('created_at', { ascending: false });

    // Appliquer le filtre domain/tenant_id
    query = applyDomainFilterToQuery(query);

    if (searchQuery) {
      query = query.or(`title.ilike.%${searchQuery}%,excerpt.ilike.%${searchQuery}%,content.ilike.%${searchQuery}%`);
    }

    const { data: posts, error } = await query;

    if (error) {
      console.error('Erreur lors de la récupération des articles:', error);
      throw error;
    }

    // Filtrer par catégorie si nécessaire
    let filteredPosts = posts || [];
    
    if (categoryId) {
      // Récupérer les IDs des articles associés à la catégorie
      const { data: postCategories, error: categoriesError } = await supabase
        .from('blog_post_categories')
        .select('post_id')
        .eq('category_id', categoryId);
      
      if (categoriesError) {
        console.error('Erreur lors de la récupération des catégories:', categoriesError);
        throw categoriesError;
      }
      
      // Filtrer les articles par ID
      const postIds = postCategories?.map(pc => pc.post_id) || [];
      filteredPosts = filteredPosts.filter(post => postIds.includes(post.id));
    }

    // Récupérer les catégories pour chaque article
    const formattedPosts = await Promise.all(filteredPosts.map(async (post) => {
      const { data: categories, error: categoriesError } = await supabase
        .from('blog_post_categories')
        .select(`
          category:blog_categories(id, name)
        `)
        .eq('post_id', post.id);
      
      if (categoriesError) {
        console.error('Erreur lors de la récupération des catégories:', categoriesError);
        return {
          ...post,
          categories: []
        };
      }
      
      return {
        ...post,
        categories: categories?.map((cat: any) => cat.category) || []
      };
    }));

    return formattedPosts;
  } catch (error) {
    console.error('Erreur lors de la récupération des articles:', error);
    throw error;
  }
};

/**
 * Récupère un article de blog par son slug
 */
export const fetchBlogPostBySlug = async (slug: string) => {
  try {
    // Récupérer l'article
    const { data: post, error } = await supabase
      .from('blog_posts')
      .select('*')
      .eq('slug', slug)
      .single();

    if (error) {
      console.error('Erreur lors de la récupération de l\'article:', error);
      throw error;
    }

    if (!post) {
      return null;
    }

    // Incrémenter le compteur de vues
    const { error: updateError } = await supabase
      .from('blog_posts')
      .update({ views_count: (post.views_count || 0) + 1 })
      .eq('id', post.id);

    if (updateError) {
      console.error('Erreur lors de la mise à jour du compteur de vues:', updateError);
    }

    // Récupérer les catégories de l'article
    const { data: categories, error: categoriesError } = await supabase
      .from('blog_post_categories')
      .select(`
        category:blog_categories(id, name)
      `)
      .eq('post_id', post.id);

    if (categoriesError) {
      console.error('Erreur lors de la récupération des catégories:', categoriesError);
      return {
        ...post,
        categories: []
      };
    }

    return {
      ...post,
      categories: categories?.map((cat: any) => cat.category) || []
    };
  } catch (error) {
    console.error('Erreur lors de la récupération de l\'article:', error);
    throw error;
  }
};

/**
 * Récupère toutes les catégories de blog
 */
export const fetchBlogCategories = async () => {
  try {
    // Récupérer directement les catégories filtrées seulement par domaine (sans fallback tenant_id)
    let categoriesQuery = supabase
      .from('blog_categories')
      .select('*')
      .order('name');

    // Appliquer le filtre domaine uniquement
    categoriesQuery = applyDomainOnlyFilterToQuery(categoriesQuery);

    const { data, error } = await categoriesQuery;

    if (error) {
      console.error('Erreur lors de la récupération des catégories:', error);
      throw error;
    }

    // Récupérer le nombre d'articles par catégorie
    const categoriesWithCount = await Promise.all(
      (data || []).map(async (category) => {
        const { count, error: countError } = await supabase
          .from('blog_post_categories')
          .select('*', { count: 'exact', head: true })
          .eq('category_id', category.id);

        if (countError) {
          console.error('Erreur lors du comptage des articles:', countError);
          return { ...category, post_count: 0 };
        }

        return { ...category, post_count: count || 0 };
      })
    );

    return categoriesWithCount;
  } catch (error) {
    console.error('Erreur lors de la récupération des catégories:', error);
    throw error;
  }
};

/**
 * Récupère les commentaires d'un article
 */
export const fetchBlogComments = async (postId: string) => {
  try {
    const { data, error } = await supabase
      .from('blog_comments')
      .select('*')
      .eq('post_id', postId)
      .eq('status', 'approved')
      .order('created_at', { ascending: false });

    if (error) {
      console.error('Erreur lors de la récupération des commentaires:', error);
      throw error;
    }

    return data || [];
  } catch (error) {
    console.error('Erreur lors de la récupération des commentaires:', error);
    throw error;
  }
};

/**
 * Récupère tous les articles de blog pour l'administration
 */
export const fetchAdminBlogPosts = async (
  viewMode: 'all' | 'published' | 'draft' = 'all', 
  searchQuery?: string,
  categoryId?: string
) => {
  try {
    // Construire la requête de base
    let query = supabase
      .from('blog_posts')
      .select(`
        id,
        title,
        slug,
        excerpt,
        content,
        featured_image_url,
        author_id,
        status,
        created_at,
        updated_at,
        views_count,
        likes_count
      `)
      .order('created_at', { ascending: false });

    // Appliquer le filtre domain/tenant_id
    query = applyDomainFilterToQuery(query);

    // Appliquer les filtres
    if (viewMode !== 'all') {
      query = query.eq('status', viewMode);
    }

    if (searchQuery) {
      query = query.or(`title.ilike.%${searchQuery}%,excerpt.ilike.%${searchQuery}%,content.ilike.%${searchQuery}%`);
    }

    // Exécuter la requête
    const { data: posts, error } = await query;

    if (error) {
      console.error('Erreur lors de la récupération des articles:', error);
      throw error;
    }

    // Récupérer les catégories pour chaque article
    const formattedPosts = await Promise.all((posts || []).map(async (post: any) => {
      const { data: categories, error: categoriesError } = await supabase
        .from('blog_post_categories')
        .select(`
          category:blog_categories(id, name)
        `)
        .eq('post_id', post.id);
      
      if (categoriesError) {
        console.error('Erreur lors de la récupération des catégories:', categoriesError);
        return {
          ...post,
          categories: []
        };
      }
      
      return {
        ...post,
        categories: categories?.map((cat: any) => cat.category) || []
      };
    }));

    // Filtrer par catégorie si nécessaire
    let filteredPosts = formattedPosts;
    
    if (categoryId) {
      filteredPosts = formattedPosts.filter(post => 
        post.categories.some((category: any) => category.id === categoryId)
      );
    }

    return filteredPosts;
  } catch (error) {
    console.error('Erreur lors de la récupération des articles:', error);
    throw error;
  }
};

/**
 * Récupère toutes les catégories de blog pour l'administration
 */
export const fetchAdminBlogCategories = async () => {
  try {
    // Récupérer directement les catégories du domaine actuel seulement (sans fallback tenant_id)
    let categoriesQuery = supabase
      .from('blog_categories')
      .select('*')
      .order('name');

    // Appliquer le filtre domaine uniquement
    categoriesQuery = applyDomainOnlyFilterToQuery(categoriesQuery);

    const { data: categories, error: categoriesError } = await categoriesQuery;

    if (categoriesError) {
      console.error('Erreur lors de la récupération des catégories:', categoriesError);
      throw categoriesError;
    }

    // Si aucune catégorie n'existe pour ce domaine, retourner un tableau vide
    if (!categories || categories.length === 0) {
      return [];
    }

    // Récupérer le nombre d'articles par catégorie (filtré par domaine uniquement)
    const categoriesWithCount = await Promise.all(
      categories.map(async (category) => {
        // Compter les articles associés à cette catégorie
        const { count, error: countError } = await supabase
          .from('blog_post_categories')
          .select('*', { count: 'exact', head: true })
          .eq('category_id', category.id);

        if (countError) {
          console.error('Erreur lors du comptage des articles:', countError);
          return { ...category, post_count: 0 };
        }

        return { ...category, post_count: count || 0 };
      })
    );

    return categoriesWithCount;
  } catch (error) {
    console.error('Erreur lors de la récupération des catégories:', error);
    throw error;
  }
};

/**
 * Récupère tous les commentaires pour l'administration
 */
export const fetchAdminBlogComments = async (statusFilter: string = 'all') => {
  try {
    let query = supabase
      .from('blog_comments')
      .select(`
        *,
        post:blog_posts(id, title, slug)
      `)
      .order('created_at', { ascending: false });

    if (statusFilter !== 'all') {
      query = query.eq('status', statusFilter);
    }

    const { data, error } = await query;

    if (error) {
      console.error('Erreur lors de la récupération des commentaires:', error);
      throw error;
    }

    return data || [];
  } catch (error) {
    console.error('Erreur lors de la récupération des commentaires:', error);
    throw error;
  }
};

/**
 * Supprime un article de blog
 */
export const deleteBlogPost = async (postId: string) => {
  try {
    // Supprimer les relations avec les catégories
    const { error: categoriesError } = await supabase
      .from('blog_post_categories')
      .delete()
      .eq('post_id', postId);
    
    if (categoriesError) {
      console.error('Erreur lors de la suppression des relations avec les catégories:', categoriesError);
      throw categoriesError;
    }
    
    // Supprimer les commentaires associés
    const { error: commentsError } = await supabase
      .from('blog_comments')
      .delete()
      .eq('post_id', postId);
    
    if (commentsError) {
      console.error('Erreur lors de la suppression des commentaires:', commentsError);
      throw commentsError;
    }
    
    // Supprimer l'article
    const { error } = await supabase
      .from('blog_posts')
      .delete()
      .eq('id', postId);

    if (error) {
      console.error('Erreur lors de la suppression de l\'article:', error);
      throw error;
    }

    return { success: true };
  } catch (error) {
    console.error('Erreur lors de la suppression de l\'article:', error);
    throw error;
  }
};
